/**
 * NotificationModal Component
 *
 * A modal dialog for displaying transaction notifications/results.
 *
 * @component
 */

import React, { useEffect } from "react";
import { colors, gradients, modalStyles } from "../styles";

const NotificationModal = ({ notification, onClose }) => {
  if (!notification) return null;

  const { message, type = "success" } = notification;

  // Auto-close after 8 seconds for success, manual close for errors
  useEffect(() => {
    if (type === "success") {
      const timer = setTimeout(() => onClose(), 8000);
      return () => clearTimeout(timer);
    }
  }, [notification, type, onClose]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [onClose]);

  const isSuccess = type === "success";
  const isError = type === "error";
  const isWarning = type === "warning";

  // Get colors based on type
  const getGradient = () => {
    if (isSuccess) return gradients.success;
    if (isError) return gradients.danger;
    if (isWarning)
      return `linear-gradient(135deg, ${colors.warning} 0%, #e0a800 100%)`;
    return gradients.info;
  };

  const getColor = () => {
    if (isSuccess) return colors.success;
    if (isError) return colors.danger;
    if (isWarning) return colors.warningText;
    return colors.info;
  };

  const getTitle = () => {
    if (isSuccess) return "Success!";
    if (isError) return "Error";
    if (isWarning) return "Partial Success";
    return "Notice";
  };

  const iconStyles = {
    width: "64px",
    height: "64px",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: "0 auto 20px",
    background: getGradient(),
    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
  };

  const titleStyles = {
    margin: "0 0 16px 0",
    fontSize: "22px",
    fontWeight: 700,
    color: getColor(),
    textAlign: "center",
  };

  const messageStyles = {
    margin: 0,
    fontSize: "15px",
    lineHeight: 1.6,
    color: colors.gray600,
    textAlign: "center",
    padding: "0 10px",
  };

  const buttonStyles = {
    display: "block",
    width: "100%",
    padding: "14px 24px",
    marginTop: "24px",
    border: "none",
    borderRadius: "6px",
    fontSize: "15px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: getGradient(),
    color: colors.white,
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
  };

  // Render appropriate icon based on type
  const renderIcon = () => {
    if (isSuccess) {
      return (
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke={colors.white}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      );
    }
    if (isError) {
      return (
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke={colors.white}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      );
    }
    if (isWarning) {
      return (
        <svg
          width="32"
          height="32"
          viewBox="0 0 24 24"
          fill="none"
          stroke={colors.white}
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
          <line x1="12" y1="9" x2="12" y2="13" />
          <line x1="12" y1="17" x2="12.01" y2="17" />
        </svg>
      );
    }
    // Info icon
    return (
      <svg
        width="32"
        height="32"
        viewBox="0 0 24 24"
        fill="none"
        stroke={colors.white}
        strokeWidth="3"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
    );
  };

  return (
    <div style={modalStyles.overlay} onClick={onClose}>
      <div
        style={{
          background: colors.white,
          borderRadius: "12px",
          maxWidth: "420px",
          width: "90%",
          padding: "32px",
          boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          textAlign: "center",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Icon */}
        <div style={iconStyles}>{renderIcon()}</div>

        {/* Title */}
        <h3 style={titleStyles}>{getTitle()}</h3>

        {/* Message */}
        <p style={messageStyles}>{message}</p>

        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          style={buttonStyles}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = "0 4px 12px rgba(0,0,0,0.15)";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.1)";
          }}
        >
          {isSuccess ? "Continue" : "Close"}
        </button>

        {/* Auto-close indicator for success */}
        {isSuccess && (
          <p
            style={{
              margin: "12px 0 0",
              fontSize: "12px",
              color: colors.gray500,
            }}
          >
            This will close automatically
          </p>
        )}
      </div>
    </div>
  );
};

export default NotificationModal;
